import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useFormatDate } from "@shared/hooks/useFormatDate";
import { useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EditMenu from "../../../../components/DropdownEditMenu";
import ConfirmationModal from "../../../../components/shared/ConfirmationModal";
import { usePostStore } from "../../store/postStore";
import type { Comment, Post } from "../../types/post.types";

interface PostCommentsProps {
  post: Post;
}

export function PostComments({ post }: PostCommentsProps) {
  const { user, getProfileThumbnail, profile } = useProfile();
  const { addComment, getComments, deleteComment, isDeletingComment } =
    usePostStore();
  const [comment, setComment] = useState("");
  const { formatRelativeTime } = useFormatDate();
  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalComments, setTotalComments] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allComments, setAllComments] = useState<Comment[] | undefined>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      await addComment(post.id, comment);
      setComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const loadMoreComments = async () => {
    if (isLoadingMore || !hasNextPage) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const { total, hasNextPage: hasMore } = await getComments(
        post.id,
        nextPage
      );
      setCurrentPage(nextPage);
      setHasNextPage(hasMore);
      setTotalComments(total);
    } catch (error) {
      console.error("Error loading more comments:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    setAllComments(post.commentList);
    setTotalComments(post.comments);
    setHasNextPage(post.comments > post.commentList?.length);
  }, [post.commentList, post.comments]);

  return (
    <>
      <div className="mt-4">
        {/* Comments List */}
        {post.commentList && post.commentList.length > 0 ? (
          <div className="mt-3 mb-3">
            <div className="mt-5 pt-3 border-t border-gray-100 dark:border-gray-700/60">
              <ul className="space-y-2 mb-3">
                {allComments?.map((comment) => (
                  <li
                    key={comment.id}
                    className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded"
                  >
                    <div className="flex items-start space-x-3">
                      <img
                        className="rounded-full shrink-0"
                        src={getProfileThumbnail(comment.author)}
                        width="32"
                        height="32"
                        alt={comment.author.name}
                      />
                      <div className="w-full">
                        <div
                          className={`text-xs ${theme.text.muted} flex items-center justify-between w-full`}
                        >
                          <div>
                            <Link
                              to={`/community/profile/${comment.author.id}`}
                              className={`font-semibold ${theme.text.primary}`}
                            >
                              {comment.author.name}
                            </Link>{" "}
                            · {formatRelativeTime(comment.createdAt)}
                          </div>
                          {comment.author.id === user?.uid && (
                            <EditMenu
                              align="right"
                              className="relative inline-flex left-0"
                            >
                              <li>
                                <button
                                  className={`font-medium text-sm ${theme.text.error} hover:text-red-600 flex py-1 px-3 items-center disabled:opacity-50`}
                                  onClick={() => {
                                    setCommentToDelete(comment);
                                    setShowDeleteConfirmation(true);
                                  }}
                                  disabled={isDeletingComment}
                                >
                                  <svg
                                    className={`w-4 h-4 fill-current ${theme.text.error} shrink-0`}
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
                                  </svg>
                                  <span className="ml-2">
                                    {isDeletingComment
                                      ? "Deleting..."
                                      : "Delete"}
                                  </span>
                                </button>
                              </li>
                            </EditMenu>
                          )}
                        </div>
                        <div className="text-sm">{comment.content}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Comments footer */}
              <div className="flex justify-between space-x-2">
                <div className={`text-sm ${theme.text.muted}`}>
                  <span className="font-medium text-gray-600 dark:text-gray-300">
                    {allComments?.length ?? 0}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium text-gray-600 dark:text-gray-300">
                    {totalComments}
                  </span>{" "}
                  comments
                </div>
                {hasNextPage && (
                  <button
                    onClick={loadMoreComments}
                    disabled={isLoadingMore}
                    className={`text-sm font-medium ${theme.text.brand} hover:text-violet-600 dark:hover:text-violet-400 disabled:opacity-50`}
                  >
                    {isLoadingMore ? "Loading..." : "View More Comments"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}

        {/* Comment Input */}
        <form onSubmit={handleSubmit} className="flex space-x-3">
          <img
            className="h-8 w-8 rounded-full"
            src={getProfileThumbnail(profile)}
            alt={profile?.name || "User"}
          />
          <div className="flex-grow relative">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              className="form-input w-full bg-gray-100 dark:bg-gray-700 border-transparent dark:border-transparent focus:bg-white dark:focus:bg-gray-800 placeholder-gray-500"
            />
            <div className="absolute inset-0 left-auto flex items-center">
              <button
                className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200"
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                <svg
                  className={`${theme.bg.fill} mr-2`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className="absolute z-10 mt-11 pl-8">
              <Picker
                data={data}
                onEmojiSelect={(emoji: any) => {
                  setComment(comment + emoji.native);
                  setShowEmojiPicker(false);
                }}
                onClickOutside={() => false && setShowEmojiPicker(false)}
              />
            </div>
          )}
        </form>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setCommentToDelete(null);
        }}
        onConfirm={async () => {
          if (commentToDelete) {
            await deleteComment(post.id, commentToDelete.id);
            setShowDeleteConfirmation(false);
            setCommentToDelete(null);
          }
        }}
        isLoading={isDeletingComment}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}
